import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Thermometer, Heart, Activity, Wind, Scale } from 'lucide-react';

/**
 * Composant réutilisable pour afficher les graphiques des constantes vitales
 * Utilisé par NursePatientDetail et DoctorPatientDetail
 */
export default function VitalSignsGraphs({ checkins, patientHeight }) {
  // Prepare data for charts - reverse to show oldest first
  const chartData = [...checkins]
    .reverse()
    .filter(c => c.vitals)
    .map(c => ({
      date: new Date(c.createdAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }),
      fullDate: new Date(c.createdAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }),
      temperature: c.vitals?.temperature ? parseFloat(c.vitals.temperature) : null,
      heartRate: c.vitals?.heartRate ? parseInt(c.vitals.heartRate) : null,
      spo2: c.vitals?.spo2 ? parseInt(c.vitals.spo2) : null,
      weight: c.vitals?.weight ? parseFloat(c.vitals.weight) : null,
      systole: c.vitals?.bloodPressure ? parseInt(c.vitals.bloodPressure.split("/")[0]) : null,
      diastole: c.vitals?.bloodPressure ? parseInt(c.vitals.bloodPressure.split("/")[1]) : null,
      imc: c.imc ? parseFloat(c.imc) : null,
    }));

  // Filter out entries where all values are null
  const hasData = chartData.length > 0 && chartData.some(d => 
    d.temperature || d.heartRate || d.spo2 || d.weight || d.systole
  );

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-xl shadow-lg border border-slate-200">
          <p className="text-sm font-medium text-slate-800 mb-2">{payload[0]?.payload?.fullDate}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: <span className="font-semibold">{entry.value}</span>
              {entry.dataKey === 'temperature' && '°C'}
              {entry.dataKey === 'heartRate' && ' bpm'}
              {entry.dataKey === 'spo2' && '%'}
              {entry.dataKey === 'weight' && ' kg'}
              {(entry.dataKey === 'systole' || entry.dataKey === 'diastole') && ' mmHg'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (!hasData) {
    return (
      <div className="dash-card text-center py-12">
        <TrendingUp className="w-12 h-12 mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">Pas assez de données pour afficher les graphiques</p>
        <p className="text-sm text-slate-400 mt-1">Les graphiques apparaîtront après quelques check-ins</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Temperature Chart */}
      {chartData.some(d => d.temperature) && (
        <div className="dash-card">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Thermometer className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
            Évolution de la température
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis domain={[35, 42]} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="temperature" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  dot={{ fill: '#ef4444', strokeWidth: 2 }}
                  name="Température"
                  connectNulls
                />
                <Line 
                  type="monotone"
                  dataKey={() => 38}
                  stroke="#fbbf24"
                  strokeDasharray="5 5"
                  strokeWidth={1}
                  dot={false}
                  name="Seuil fièvre"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Heart Rate Chart */}
      {chartData.some(d => d.heartRate) && (
        <div className="dash-card">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
            Évolution du pouls
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis domain={[40, 140]} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="heartRate" 
                  stroke="#ec4899" 
                  strokeWidth={2}
                  dot={{ fill: '#ec4899', strokeWidth: 2 }}
                  name="Pouls"
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Blood Pressure Chart */}
      {chartData.some(d => d.systole) && (
        <div className="dash-card">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
            Évolution de la tension artérielle
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis domain={[50, 200]} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="systole" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', strokeWidth: 2 }}
                  name="Systolique"
                  connectNulls
                />
                <Line 
                  type="monotone" 
                  dataKey="diastole" 
                  stroke="#06b6d4" 
                  strokeWidth={2}
                  dot={{ fill: '#06b6d4', strokeWidth: 2 }}
                  name="Diastolique"
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* SpO2 Chart */}
      {chartData.some(d => d.spo2) && (
        <div className="dash-card">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Wind className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
            Évolution de l'oxygène (SpO2)
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis domain={[85, 100]} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="spo2" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  dot={{ fill: '#10b981', strokeWidth: 2 }}
                  name="SpO2"
                  connectNulls
                />
                <Line 
                  type="monotone"
                  dataKey={() => 95}
                  stroke="#fbbf24"
                  strokeDasharray="5 5"
                  strokeWidth={1}
                  dot={false}
                  name="Seuil bas"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Weight & IMC Chart */}
      {chartData.some(d => d.weight) && (
        <div className="dash-card">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Scale className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
            Évolution du poids
            {patientHeight && <span className="text-sm font-normal text-slate-500">(Taille: {patientHeight} cm)</span>}
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} stroke="#94a3b8" domain={[15, 40]} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="weight" 
                  stroke="#8b5cf6" 
                  strokeWidth={2}
                  dot={{ fill: '#8b5cf6', strokeWidth: 2 }}
                  name="Poids (kg)"
                  connectNulls
                />
                {chartData.some(d => d.imc) && (
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="imc" 
                    stroke="#f97316" 
                    strokeWidth={2}
                    dot={{ fill: '#f97316', strokeWidth: 2 }}
                    name="IMC"
                    connectNulls
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
